import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, User } from 'lucide-react';
import usePolling from '@/hooks/usePolling';
import pollGet from '@/utils/pollGet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalize = (n) => (n || '').toUpperCase().trim().replace(/\s+/g, ' ');

const ResumenIncumplimientoPage = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await pollGet(`${API}/tutor/student-data`, {
        key: 'resumen-student-data', conditional: !!silent, headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.notModified) setData(res.data);
    } catch (e) {
      // Ignore transient errors on purpose.
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);
  usePolling(() => fetchData(true), { intervalMs: 12000 });

  // ---- Build the incumplimiento summary grouped by child, then subject ----
  const buildSummary = () => {
    if (!data) return [];
    const students = data.students || [];
    const attendance = data.attendance || [];
    const tasks = data.tasks || [];

    const nameById = {};
    const matriculaByName = {};
    students.forEach(s => {
      nameById[s.id] = s.name;
      if (s.matricula) matriculaByName[normalize(s.name)] = s.matricula;
    });

    // key: `${childName}||${class_id}` -> aggregate
    const bySubject = {};
    const ensure = (childName, classId, subject, teacherName) => {
      const key = `${childName}||${classId}`;
      if (!bySubject[key]) {
        bySubject[key] = { childName, subject, teacherName, faltas: 0, totalAtt: 0, failedTasks: [] };
      }
      return bySubject[key];
    };

    // Attendance -> faltas (exclude separators & suspensions)
    attendance.forEach(entry => {
      const dataCols = (entry.columns || []).filter(c => !c.isSeparator && !c.isSuspension);
      Object.entries(entry.records || {}).forEach(([sid, rec]) => {
        const childName = nameById[sid];
        if (!childName) return;
        const agg = ensure(childName, entry.class_id, entry.subject, entry.teacher_name);
        agg.totalAtt = dataCols.length;
        let faltas = 0;
        dataCols.forEach(col => {
          const val = String(rec[col.id] ?? '').trim();
          if (val === '0') faltas += 1;
        });
        agg.faltas = faltas;
      });
    });

    // Tasks -> failed (<=50)
    tasks.forEach(entry => {
      const dataCols = (entry.columns || []).filter(c => !c.isSeparator);
      Object.entries(entry.records || {}).forEach(([sid, rec]) => {
        const childName = nameById[sid];
        if (!childName) return;
        const agg = ensure(childName, entry.class_id, entry.subject, entry.teacher_name);
        dataCols.forEach(col => {
          const raw = rec[col.id];
          if (raw === undefined || raw === null || String(raw).trim() === '') return;
          const num = parseInt(String(raw).trim(), 10);
          if (isNaN(num)) return;
          if (num <= 50) {
            agg.failedTasks.push({
              label: col.label || `Tarea ${col.number ?? ''}`.trim(),
              date: col.date || '',
              description: col.description || '',
              value: num
            });
          }
        });
      });
    });

    // Group by child, keep only subjects with faltas>0 OR failed tasks
    const children = {};
    Object.values(bySubject).forEach(agg => {
      if (agg.faltas > 0 || agg.failedTasks.length > 0) {
        const key = normalize(agg.childName);
        if (!children[key]) {
          children[key] = { name: agg.childName, matricula: matriculaByName[key] || '', subjects: [] };
        }
        children[key].subjects.push(agg);
      }
    });

    return Object.values(children)
      .map(c => ({ ...c, subjects: c.subjects.sort((a, b) => a.subject.localeCompare(b.subject)) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const summary = buildSummary();
  const totalChildren = (data?.students || []).reduce((acc, s) => {
    const key = normalize(s.name);
    return acc.includes(key) ? acc : [...acc, key];
  }, []).length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-6 h-6 text-rose-500 mt-1 shrink-0" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="resumen-incumplimiento-title">
                Resumen de Incumplimiento
              </h1>
              <p className="text-sm text-slate-600 mt-1">Materias donde tu hijo(a) tiene al menos una falta o alguna tarea calificada con 50 o menos. Se actualiza solo conforme los maestros cargan datos.</p>
            </div>
          </div>
          <Button onClick={() => fetchData(false)} variant="outline" size="sm" className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="resumen-incumplimiento-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center h-40"><RefreshCw className="w-7 h-7 animate-spin text-rose-500" /></div>
        ) : summary.length === 0 ? (
          <Card className="p-10 text-center" data-testid="resumen-incumplimiento-empty">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-7 h-7 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-1">Sin incumplimientos por ahora 👌</h3>
            <p className="text-slate-500 text-sm">
              {totalChildren === 0
                ? 'Aún no hay alumnos vinculados a tu cuenta.'
                : 'Ninguna materia registra faltas ni tareas reprobadas.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-8">
            {summary.map((child, ci) => (
              <div key={ci} data-testid={`resumen-child-${ci}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-800 text-white flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{child.name}</h2>
                    {child.matricula && <p className="text-xs text-slate-400">{child.matricula}</p>}
                  </div>
                </div>
                <div className="space-y-4">
                  {child.subjects.map((s, si) => (
                    <Card key={si} className="overflow-hidden" data-testid={`resumen-materia-${ci}-${si}`}>
                      <div className="flex items-center justify-between bg-slate-100 px-4 py-2.5 border-b border-slate-200">
                        <div>
                          <h4 className="font-semibold text-slate-800">{s.subject}</h4>
                          {s.teacherName && <p className="text-xs text-slate-500">Prof. {s.teacherName}</p>}
                        </div>
                      </div>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Faltas</p>
                          {s.faltas > 0 ? (
                            <span className="inline-flex items-center font-bold text-rose-700 bg-rose-100 border border-rose-200 rounded-lg px-3 py-1">
                              {s.faltas}<span className="text-[11px] font-normal text-rose-400 ml-1">/ {s.totalAtt} clases</span>
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">Sin faltas</span>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Tareas reprobadas (≤50)</p>
                          {s.failedTasks.length === 0 ? (
                            <span className="text-slate-400 text-sm">Ninguna</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {s.failedTasks.map((t, ti) => (
                                <span key={ti} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2 py-0.5 text-xs" title={`${t.description || t.label}${t.date ? ' · ' + t.date : ''}`}>
                                  {t.label} <strong className="text-rose-600">{t.value}%</strong>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ResumenIncumplimientoPage;
